import type { Locale } from "@tutor/protocol";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import * as api from "./api";
import "./auth.css";
import { commonErrorKey } from "./errors";
import { ParentSetup, TextStep } from "./ParentSetup";
import { PinPad } from "./PinPad";
import { Message, Screen } from "./Screen";
import { clearDevice, loadDevice, saveDevice, saveLastProfile, type DeviceRecord } from "./storage";
import { detectLocale, translator, type AuthStringKey } from "./strings";

/** A child signed in on this device: what the live channel needs. */
export interface LearnerSession {
  readonly profileId: string;
  /** Learner token (ADR-0019); sent as the `auth.<token>` WebSocket subprotocol. */
  readonly token: string;
  /** Milliseconds since the epoch. */
  readonly expiresAt: number;
  readonly locale: Locale;
  /** The API allows voice mode for this household; the microphone is hidden otherwise. */
  readonly voiceEntitled: boolean;
}

export interface SessionActions {
  /** Back to the PIN pad for the same child. */
  readonly lock: () => void;
  /** A newer token for the same child. */
  readonly refreshed: (session: LearnerSession) => void;
}

type View =
  | { readonly kind: "loading" }
  | { readonly kind: "welcome"; readonly notice?: AuthStringKey }
  | { readonly kind: "parent" }
  | { readonly kind: "pairing" }
  | { readonly kind: "unavailable"; readonly error: AuthStringKey }
  | { readonly kind: "profiles"; readonly profiles: readonly api.DeviceProfile[] }
  | {
      readonly kind: "pin";
      readonly profile: api.DeviceProfile;
      readonly profiles: readonly api.DeviceProfile[];
    }
  | {
      readonly kind: "session";
      readonly session: LearnerSession;
      readonly profile: api.DeviceProfile;
      readonly profiles: readonly api.DeviceProfile[];
    };

type PinError =
  | "pad.wrong"
  | "pad.locked"
  | "pad.notSet"
  | "common.offline"
  | "common.rateLimited"
  | "common.error";

/**
 * Everything between opening the app and a signed-in child: device set-up by a parent
 * or with a pairing code, "who is learning?", and the PIN pad. Renders `children` once
 * a learner session exists.
 */
export function AuthGate(props: {
  children: (session: LearnerSession, actions: SessionActions) => ReactNode;
}): ReactElement {
  const [view, setView] = useState<View>({ kind: "loading" });
  const [device, setDevice] = useState<DeviceRecord>();
  const [setupLocale, setSetupLocale] = useState<Locale>(detectLocale);
  const [pinError, setPinError] = useState<PinError>();
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);

  const locale: Locale =
    view.kind === "pin"
      ? view.profile.locale
      : view.kind === "session"
        ? view.session.locale
        : setupLocale;
  const t = useMemo(() => translator(locale), [locale]);

  const forgetDevice = useCallback(async (notice: AuthStringKey) => {
    await clearDevice();
    setDevice(undefined);
    setView({ kind: "welcome", notice });
  }, []);

  /** Loads the device's profiles and shows the picker, or the PIN pad of the last child. */
  const openDevice = useCallback(
    async (record: DeviceRecord, lastProfileId: string | undefined) => {
      setView({ kind: "loading" });
      const res = await api.getDeviceProfiles(record.credential);
      if (!res.ok) {
        if (res.error.kind === "http" && res.error.status === 401) {
          await forgetDevice("welcome.deviceRemoved");
        } else {
          setView({ kind: "unavailable", error: commonErrorKey(res.error) });
        }
        return;
      }
      const { profiles } = res.value;
      const only = profiles.length === 1 ? profiles[0] : undefined;
      const last = profiles.find((p) => p.profileId === lastProfileId) ?? only;
      setPinError(undefined);
      if (profiles.length === 0) setView({ kind: "welcome", notice: "welcome.noProfiles" });
      else if (last !== undefined) setView({ kind: "pin", profile: last, profiles });
      else setView({ kind: "profiles", profiles });
    },
    [forgetDevice],
  );

  useEffect(() => {
    let live = true;
    void loadDevice().then(({ device: stored, lastProfileId }) => {
      if (!live) return;
      setDevice(stored);
      if (stored === undefined) setView({ kind: "welcome" });
      else void openDevice(stored, lastProfileId);
    });
    return () => {
      live = false;
    };
  }, [openDevice]);

  const enrolled = useCallback(
    async (record: DeviceRecord) => {
      await saveDevice(record);
      setDevice(record);
      await openDevice(record, undefined);
    },
    [openDevice],
  );

  const pickProfile = (profile: api.DeviceProfile, profiles: readonly api.DeviceProfile[]) => {
    setPinError(profile.pinSet ? undefined : "pad.notSet");
    setView({ kind: "pin", profile, profiles });
    void saveLastProfile(profile.profileId);
  };

  const submitPin = async (pin: string) => {
    if (view.kind !== "pin" || device === undefined || busy) return;
    const { profile, profiles } = view;
    setBusy(true);
    const res = await api.startLearnerSession(device.credential, profile.profileId, pin);
    setBusy(false);
    if (res.ok) {
      const expiresAt =
        typeof res.value.expiresAt === "number"
          ? res.value.expiresAt
          : Date.parse(res.value.expiresAt);
      setPinError(undefined);
      setView({
        kind: "session",
        profile,
        profiles,
        session: {
          profileId: profile.profileId,
          token: res.value.token,
          expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now(),
          locale: res.value.locale ?? profile.locale,
          voiceEntitled: res.value.voiceEntitled ?? false,
        },
      });
      return;
    }
    setAttempt((n) => n + 1);
    const { error } = res;
    if (error.kind === "offline") {
      setPinError("common.offline");
      return;
    }
    if (error.kind === "rate_limited") {
      setPinError("common.rateLimited");
      return;
    }
    if (error.status === 401 && error.code === "unauthenticated") {
      await forgetDevice("welcome.deviceRemoved");
    } else if (error.status === 401) {
      setPinError("pad.wrong");
    } else if (error.status === 423) {
      setPinError("pad.locked");
    } else if (error.status === 409) {
      setPinError("pad.notSet");
    } else if (error.status === 403) {
      // The profile left this device: show the current list.
      await openDevice(device, undefined);
    } else {
      setPinError("common.error");
    }
  };

  const lock = useCallback(() => {
    setView((v) =>
      v.kind === "session" ? { kind: "pin", profile: v.profile, profiles: v.profiles } : v,
    );
    setPinError(undefined);
    setAttempt((n) => n + 1);
  }, []);

  const refreshed = useCallback((session: LearnerSession) => {
    setView((v) =>
      v.kind === "session" && v.session.profileId === session.profileId ? { ...v, session } : v,
    );
  }, []);

  const actions = useMemo<SessionActions>(() => ({ lock, refreshed }), [lock, refreshed]);

  // An expired learner token cannot open the live channel: ask for the PIN again.
  const expiresAt = view.kind === "session" ? view.session.expiresAt : undefined;
  useEffect(() => {
    if (expiresAt === undefined) return;
    const timer = setTimeout(lock, Math.max(0, expiresAt - Date.now()));
    return () => {
      clearTimeout(timer);
    };
  }, [expiresAt, lock]);

  const languageToggle = (
    <button
      type="button"
      className="auth-link"
      lang={setupLocale === "en" ? "fr" : "en"}
      aria-label={t("common.switchLanguageLabel")}
      onClick={() => {
        setSetupLocale((l) => (l === "en" ? "fr" : "en"));
      }}
    >
      {t("common.switchLanguage")}
    </button>
  );

  let content: ReactNode;
  switch (view.kind) {
    case "loading":
      content = <Screen title={t("common.loading")} />;
      break;

    case "welcome":
      content = (
        <Screen title={t("welcome.title")} body={t("welcome.body")} footer={languageToggle}>
          <Message text={view.notice === undefined ? undefined : t(view.notice)} />
          <div className="auth-choices">
            <button
              type="button"
              className="auth-choice"
              onClick={() => {
                setView({ kind: "parent" });
              }}
            >
              <span className="auth-choice-title">{t("welcome.parentHere")}</span>
              <span className="auth-choice-hint">{t("welcome.parentHereHint")}</span>
            </button>
            <button
              type="button"
              className="auth-choice"
              onClick={() => {
                setView({ kind: "pairing" });
              }}
            >
              <span className="auth-choice-title">{t("welcome.pairing")}</span>
              <span className="auth-choice-hint">{t("welcome.pairingHint")}</span>
            </button>
          </div>
        </Screen>
      );
      break;

    case "parent":
      content = (
        <ParentSetup
          t={t}
          locale={setupLocale}
          onEnrolled={(record) => void enrolled(record)}
          onCancel={() => {
            setView({ kind: "welcome" });
          }}
        />
      );
      break;

    case "pairing":
      content = (
        <Pairing
          t={t}
          onEnrolled={(record) => void enrolled(record)}
          onBack={() => {
            setView({ kind: "welcome" });
          }}
        />
      );
      break;

    case "unavailable":
      content = (
        <Screen title={t("common.unavailable")}>
          <Message text={t(view.error)} />
          <button
            type="button"
            className="auth-button auth-button-primary"
            onClick={() => {
              void loadDevice().then(({ device: stored, lastProfileId }) => {
                if (stored === undefined) setView({ kind: "welcome" });
                else void openDevice(stored, lastProfileId);
              });
            }}
          >
            {t("common.retry")}
          </button>
        </Screen>
      );
      break;

    case "profiles": {
      const { profiles } = view;
      content = (
        <Screen title={t("profiles.title")} footer={languageToggle}>
          <ul className="auth-profiles">
            {profiles.map((p) => (
              <li key={p.profileId}>
                <button
                  type="button"
                  className="auth-profile"
                  lang={p.locale}
                  aria-label={t("profiles.pick", { name: p.displayName })}
                  onClick={() => {
                    pickProfile(p, profiles);
                  }}
                >
                  <span className="auth-avatar" aria-hidden="true">
                    {initial(p.displayName)}
                  </span>
                  <span className="auth-profile-name">{p.displayName}</span>
                  {p.pinSet ? null : (
                    <span className="auth-profile-hint">{t("profiles.noPin")}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Screen>
      );
      break;
    }

    case "pin": {
      const { profile, profiles } = view;
      content = (
        <Screen
          title={t("pad.title", { name: profile.displayName })}
          body={t("pad.body")}
          footer={
            profiles.length > 1 ? (
              <button
                type="button"
                className="auth-link"
                onClick={() => {
                  setPinError(undefined);
                  setView({ kind: "profiles", profiles });
                }}
              >
                {t("pad.notYou")}
              </button>
            ) : undefined
          }
        >
          <Message text={pinError === undefined ? undefined : t(pinError)} />
          <PinPad
            // A new pad (no digits) after each attempt.
            key={attempt}
            t={t}
            busy={busy}
            disabled={pinError === "pad.locked" || pinError === "pad.notSet"}
            onSubmit={(pin) => void submitPin(pin)}
          />
        </Screen>
      );
      break;
    }

    case "session":
      return <>{props.children(view.session, actions)}</>;
  }

  return (
    <div className="auth-root" lang={locale}>
      {content}
    </div>
  );
}

function initial(name: string): string {
  return Array.from(name.trim())[0]?.toLocaleUpperCase() ?? "?";
}

function Pairing(props: {
  t: ReturnType<typeof translator>;
  onEnrolled: (record: DeviceRecord) => void;
  onBack: () => void;
}) {
  const { t } = props;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<AuthStringKey>();
  return (
    <TextStep
      t={t}
      title={t("pairing.title")}
      body={t("pairing.body")}
      label={t("pairing.label")}
      submit={t("pairing.submit")}
      initial=""
      autoCapitalize="characters"
      busy={busy}
      message={message === undefined ? undefined : { key: message }}
      footer={
        <button type="button" className="auth-link" onClick={props.onBack} disabled={busy}>
          {t("common.back")}
        </button>
      }
      onSubmit={(code) => {
        if (busy) return;
        setBusy(true);
        void api.redeemPairingCode(code, t("enroll.defaultLabel")).then((res) => {
          setBusy(false);
          if (res.ok) {
            props.onEnrolled({ deviceId: res.value.deviceId, credential: res.value.credential });
          } else {
            setMessage(
              res.error.kind === "http" && res.error.status === 400
                ? "pairing.invalid"
                : commonErrorKey(res.error),
            );
          }
        });
      }}
    />
  );
}
